import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverTeams() {
  const { api, AdminId } = useGlobalContext();

  const [teams, setTeams] = useState([]);
  const [driverTeams, setDriverTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamMembersByTeamId, setTeamMembersByTeamId] = useState({});
  const [teamMembersLoadingId, setTeamMembersLoadingId] = useState(null);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "01" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  /* ================= GET ALL TEAMS ================= */

  const fetchDriverTeams = useCallback(async () => {
    setTeamsLoading(true);

    try {
      const res = await api.post("/api/Driver/v2.0/getDriverTeamList", {
        userid: "",
        adminId: String(AdminId),
      });

      if (res.data?.responseCode === "00") {
        const data = res.data?.data || [];
        setTeams(data);
        return data;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch teams");
      }

      setTeams([]);
      return [];
    } catch (error) {
      console.error("fetchDriverTeams error:", error);
      toast.error("Failed to fetch teams");
      setTeams([]);
      return [];
    } finally {
      setTeamsLoading(false);
    }
  }, [api, AdminId]);

  /* ================= GET DRIVER MULTIPLE TEAMS ================= */

  const fetchDriverMultipleTeams = async (driverId) => {
    if (!driverId) {
      setDriverTeams([]);
      return [];
    }

    setLoadingTeams(true);

    try {
      const res = await api.post("/api/Driver/v1.0/getDriverMultipleTeams", {
        driverId,
      });

      if (res.data?.responseCode === "00") {
        const teams = res.data?.data?.[0]?.driverTeams || [];
        setDriverTeams(teams);
        return teams;
      }

      if (res.data?.responseCode === "02" || res.data?.responseCode === "03") {
        setDriverTeams([]);
        return [];
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch driver teams");
      }

      setDriverTeams([]);
      return [];
    } catch (err) {
      console.error("fetchDriverMultipleTeams error:", err);
      toast.error("Error fetching driver teams");
      setDriverTeams([]);
      return [];
    } finally {
      setLoadingTeams(false);
    }
  };

  /* ================= UPDATE DRIVER ACTIVE TEAM ================= */

  const updateDriverActiveTeam = async ({ adminId, driverId, teamId }) => {
    if (!adminId || !driverId || !teamId) {
      toast.error("Missing required team data");
      return null;
    }

    try {
      const res = await api.post("/api/Driver/v1.0/updateDriverActiveTeam", {
        adminId,
        driverId,
        teamId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Team activated successfully");
        return res.data;
      }

      if (res.data?.responseCode === "01") {
        return res.data;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to update team");
      }

      return res.data || null;
    } catch (err) {
      console.error("updateDriverActiveTeam error:", err);
      toast.error("Error updating team");
      return null;
    }
  };

  /* ================= INSERT DRIVER MULTIPLE TEAMS ================= */

  const insertDriverMultipleTeams = async ({
    driverId,
    adminId,
    teamIds,
    primaryTeamId,
  }) => {
    if (!driverId || !adminId || !Array.isArray(teamIds)) {
      toast.error("Missing required team assignment data");
      return false;
    }

    try {
      const payload = {
        driverId,
        adminId: String(adminId),
        teamId: teamIds.map((id) => String(id)),
        primaryTeamId: String(primaryTeamId || ""),
      };

      const res = await api.post(
        "/api/Driver/v1.0/insertDriverMultipleTeams",
        payload
      );

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Teams assigned successfully");
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to assign teams");
      }

      return false;
    } catch (err) {
      console.error("insertDriverMultipleTeams error:", err);
      toast.error("Error assigning teams");
      return false;
    }
  };

  /* ================= DELETE DRIVER MULTIPLE TEAMS ================= */

  const deleteDriverMultipleTeams = async ({ driverId, adminId, teamIds }) => {
    if (!driverId || !Array.isArray(teamIds)) {
      toast.error("Missing required delete team data");
      return false;
    }

    try {
      const payload = {
        driverId,
        adminId: String(adminId || AdminId),
        teamId: teamIds.map((id) => String(id)),
      };

      const res = await api.post(
        "/api/Driver/v1.0/deleteDriverMultipleTeams",
        payload
      );

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || "Driver team deleted successfully"
        );
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to delete driver team");
      }

      return false;
    } catch (err) {
      console.error("deleteDriverMultipleTeams error:", err);
      toast.error("Error deleting driver team");
      return false;
    }
  };

  /* ================= GET TEAM MEMBERS BY TEAM ID ================= */

  const fetchDriverTeamListByTeamId = async (teamId, force = false) => {
    if (!teamId) return null;

    if (!force && teamMembersByTeamId[teamId]) {
      return teamMembersByTeamId[teamId];
    }

    setTeamMembersLoadingId(teamId);

    try {
      const res = await api.post("/api/Driver/v1.0/getAllDriverTeamListByTeamId", {
        teamId: String(teamId),
      });

      if (res.data?.responseCode === "00") {
        const raw = Array.isArray(res.data?.data) ? res.data.data[0] || {} : {};

        const normalized = {
          primaryTeamDrivers: Array.isArray(raw?.primaryTeamDrivers)
            ? raw.primaryTeamDrivers
            : [],
          secondaryTeamDrivers: Array.isArray(raw?.secondaryTeamDrivers)
            ? raw.secondaryTeamDrivers
            : [],
          activeTeamDrivers: Array.isArray(raw?.activeTeamDrivers)
            ? raw.activeTeamDrivers
            : [],
        };

        setTeamMembersByTeamId((prev) => ({
          ...prev,
          [teamId]: normalized,
        }));

        return normalized;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch team member details"
        );
      }

      return null;
    } catch (err) {
      console.error("fetchDriverTeamListByTeamId error:", err);
      toast.error("Error fetching team member details");
      return null;
    } finally {
      setTeamMembersLoadingId(null);
    }
  };

  return {
    teams,
    teamsLoading,
    fetchDriverTeams,

    driverTeams,
    loadingTeams,
    fetchDriverMultipleTeams,
    updateDriverActiveTeam,
    insertDriverMultipleTeams,
    deleteDriverMultipleTeams,

    teamMembersByTeamId,
    teamMembersLoadingId,
    fetchDriverTeamListByTeamId,
  };
}